import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, FileText, FileCode, FileArchive, Download } from "lucide-react";
import { api, imgSrc, imgProps, fileUrl } from "../lib/api";
import { useLang } from "../i18n";
import { Reveal, Eyebrow } from "../components/Reveal";
import DownloadGate from "../components/DownloadGate";
import BeforeAfter from "../components/BeforeAfter";
import ProjectGallery from "../components/project/ProjectGallery";
import TechDocsPlaceholder from "../components/project/TechDocsPlaceholder";

const CATEGORY_LABEL = { windows: { it: "Finestre", en: "Windows" }, doors: { it: "Porte", en: "Doors" }, facades: { it: "Facciate", en: "Facades" }, outdoor: { it: "Vetrate & Pergole", en: "Glass Rooms & Pergolas" }, awnings: { it: "Tende da Sole", en: "Awnings" }, railings: { it: "Balaustre in Vetro", en: "Glass Balustrades" } };

function FileIcon({ ext }) {
  if (ext === "pdf") return <FileText size={20} className="text-[#C8A86B] shrink-0" />;
  if (ext === "dwg" || ext === "dxf") return <FileCode size={20} className="text-[#C8A86B] shrink-0" />;
  return <FileArchive size={20} className="text-[#C8A86B] shrink-0" />;
}

export default function ProjectDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [project, setProject] = useState(null);
  const [next, setNext] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    setProject(null);
    setNotFound(false);
    api.get(`/projects/${id}`)
      .then(({ data }) => {
        setProject(data);
        api.get("/projects").then((res) => {
          const list = res.data;
          const idx = list.findIndex((p) => p.id === data.id);
          if (idx >= 0 && list.length > 1) setNext(list[(idx + 1) % list.length]);
        }).catch(() => {});
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="project-not-found">
        <p className="font-serif text-3xl text-[#F3EFE6]">404</p>
        <Link to="/projects" className="mt-4 inline-block text-sm uppercase tracking-[0.15em] text-[#C8A86B]">{t.back_to_projects}</Link>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="pt-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="aspect-[21/9] animate-pulse border border-[#1C1A17] bg-[#111110]" />
      </div>
    );
  }

  const title = lang === "it" ? project.title_it : project.title_en;
  const description = lang === "it" ? project.description_it : project.description_en;
  const cat = CATEGORY_LABEL[project.category]?.[lang] || project.category;

  const openGate = (file) => {
    const saved = localStorage.getItem("ai_gate");
    if (saved) {
      const a = document.createElement("a");
      a.href = fileUrl(file.storage_path, true);
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }
    setSelectedFile(file);
    setGateOpen(true);
  };

  const meta = [
    { label: t.label_location, value: project.location },
    { label: t.label_year, value: project.year },
    { label: t.label_architect, value: project.architect },
    { label: t.label_dimensions, value: project.dimensions },
  ];

  return (
    <div data-testid="project-detail-page" className="pt-28 sm:pt-36 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal y={16}>
          <Link to="/projects" data-testid="back-to-projects" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#827D72] transition-colors hover:text-[#C8A86B]">
            <ArrowLeft size={14} /> {t.back_to_projects}
          </Link>
          <Eyebrow className="mt-8">{cat} — {project.location}</Eyebrow>
          <h1 className="mt-4 max-w-4xl font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.1] text-[#F3EFE6]">{title}</h1>
        </Reveal>

        <Reveal delay={0.15} className="mt-12">
          <div className="relative overflow-hidden border border-[#262420]">
            <img {...imgProps(project.cover_image)} alt={title} className="w-full max-h-[70vh] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#080808]/50 via-transparent to-transparent" />
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1C1A17] border border-[#1C1A17]">
          {meta.map((m) => (
            <div key={m.label} className="bg-[#0A0A09] px-5 py-6">
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#C8A86B]">{m.label}</p>
              <p data-testid={`meta-${m.label.toLowerCase().replace(/\s/g, "-")}`} className="mt-3 text-sm text-[#F3EFE6] font-light leading-relaxed">{m.value || "—"}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            {description && (
              <Reveal>
                <p className="text-base sm:text-lg font-light text-[#A39E93] leading-relaxed">{description}</p>
              </Reveal>
            )}
            {project.materials?.length > 0 && (
              <Reveal delay={0.1} className="mt-10">
                <p className="text-xs uppercase tracking-[0.25em] font-mono text-[#C8A86B]">{t.label_materials}</p>
                <ul className="mt-5 flex flex-col">
                  {project.materials.map((m) => (
                    <li key={m} className="border-t border-[#1C1A17] py-3 text-sm font-light text-[#F3EFE6]">{m}</li>
                  ))}
                </ul>
              </Reveal>
            )}
          </div>

          <div className="lg:col-span-5">
            {Object.keys(project.specs || {}).length > 0 && (
              <Reveal delay={0.15}>
                <div className="border border-[#262420] bg-[#0F0F0E] p-7">
                  <p className="text-xs uppercase tracking-[0.25em] font-mono text-[#C8A86B]">{t.specs_title}</p>
                  <dl className="mt-6 flex flex-col">
                    {Object.entries(project.specs).map(([k, v]) => (
                      <div key={k} className="flex items-start justify-between gap-4 border-t border-[#1C1A17] py-3">
                        <dt className="text-xs font-mono text-[#827D72] uppercase tracking-wider">{k}</dt>
                        <dd className="text-right text-sm text-[#F3EFE6]">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </Reveal>
            )}
          </div>
        </div>

        {project.before_image && (
          <Reveal className="mt-20">
            <Eyebrow>{t.ba_eyebrow}</Eyebrow>
            <h2 className="mt-3 font-serif text-2xl sm:text-3xl text-[#F3EFE6]">{t.ba_title}</h2>
            <p className="mt-3 max-w-xl text-sm font-light text-[#A39E93]">{t.ba_sub}</p>
            <div className="mt-8">
              <BeforeAfter before={imgSrc(project.before_image)} after={imgSrc(project.cover_image)} alt={title} />
            </div>
          </Reveal>
        )}

        <ProjectGallery project={project} title={title} />

        {project.technical_files?.length > 0 && (
          <Reveal className="mt-20">
            <div className="border border-[#C8A86B]/20 bg-[#0F0F0E] p-7 sm:p-10">
              <Eyebrow>{t.downloads_title}</Eyebrow>
              <p className="mt-3 max-w-lg text-sm font-light text-[#A39E93]">{t.downloads_sub}</p>
              <ul className="mt-8 flex flex-col gap-3">
                {project.technical_files.map((f) => (
                  <li key={f.storage_path} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-[#262420] bg-[#080808] px-5 py-4 transition-colors duration-300 hover:border-[#C8A86B]/40">
                    <div className="flex items-center gap-4 min-w-0">
                      <FileIcon ext={f.ext} />
                      <div className="min-w-0">
                        <p className="truncate text-sm text-[#F3EFE6]">{f.name}</p>
                        <p className="text-xs font-mono uppercase text-[#827D72]">{f.ext} · {f.size}</p>
                      </div>
                    </div>
                    <button
                      data-testid={`download-specs-button-${f.ext}-${f.name.slice(0, 12).replace(/[^a-zA-Z0-9]/g, "-")}`}
                      onClick={() => openGate(f)}
                      className="inline-flex shrink-0 items-center gap-2 border border-[#C8A86B] px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-[#C8A86B] transition-all duration-300 hover:bg-[#C8A86B] hover:text-[#080808]"
                    >
                      <Download size={14} /> {t.download}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        )}

        <TechDocsPlaceholder project={project} />

        {next && (
          <Reveal className="mt-24">
            <div className="hairline" />
            <Link to={`/projects/${next.id}`} data-testid="next-project-link" className="group mt-12 flex items-center justify-between gap-6">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] font-mono text-[#827D72]">{t.next_project}</p>
                <p className="mt-3 font-serif text-2xl sm:text-3xl text-[#F3EFE6] transition-colors group-hover:text-[#D8BD85]">
                  {lang === "it" ? next.title_it : next.title_en}
                </p>
              </div>
              <ArrowRight size={28} className="shrink-0 text-[#C8A86B] transition-transform duration-300 group-hover:translate-x-2" />
            </Link>
          </Reveal>
        )}
      </div>

      <DownloadGate open={gateOpen} onOpenChange={setGateOpen} file={selectedFile} projectId={project.id} />
    </div>
  );
}
